import { ActionTree, GetterTree, MutationTree } from 'vuex'
import Vue from 'vue'
import { NotificationOptions } from 'vue-notification'
import {
  ICartItem,
  ICartMessage,
  ICartProgressBar,
} from '~/assets/interfaces/cart'
import { CART } from '~/assets/config/api-path'
import { IStateIndex } from '~/store/index'
import { filterUrl } from '~/assets/filters/url'
import { IProductToCart } from '~/assets/interfaces/products'
import { analyticsReachGoal } from '~/assets/functions/analytics'

interface IState {
  items: ICartItem[]
  isLoading: boolean
  isFirstLoaded: boolean
  canOrder: boolean
  bonus: number
  progressBar: ICartProgressBar | null
  message?: ICartMessage
}

const normalizeCartPayload = (
  payload: IProductToCart | IProductToCart[]
): IProductToCart | IProductToCart[] => {
  if (Array.isArray(payload)) return payload

  return {
    id: payload.id,
    quantity: payload.quantity ?? 1,
    cartId: payload.cartId || null,
    services: payload.services,
  }
}

export const state = (): IState => ({
  items: [],
  isLoading: false,
  isFirstLoaded: false,
  canOrder: false,
  bonus: 0,
  progressBar: null,
})

export const mutations: MutationTree<IState> = {
  setItems(state: IState, val: ICartItem[]) {
    state.items = val
  },
  setLoading(state: IState, val: boolean) {
    state.isLoading = val
  },
  setFirstLoaded(state: IState, val: boolean) {
    state.isFirstLoaded = val
  },
  setCanOrder(state: IState, val: boolean) {
    state.canOrder = val
  },
  setBonus(state: IState, val: number) {
    state.bonus = Number(val)
  },
  setProgressBar(state: IState, val: ICartProgressBar | null) {
    state.progressBar = val
  },
  setMessage(state: IState, val: ICartMessage) {
    if (val) {
      Vue.set(state, 'message', val)
    } else {
      Vue.delete(state, 'message')
    }
  },
}

export const getters: GetterTree<IState, IStateIndex> = {
  itemsQuantity(state: IState): number {
    return state.items.length
  },
  getMessageText(state: IState): string | null {
    return state.message ? state.message.text : null
  },
}

export const actions: ActionTree<IState, IStateIndex> = {
  async getItems({ commit }) {
    commit('setLoading', true)
    const response = await this.$axios.$get(filterUrl(CART))

    // Коммитим сразу, а не через runIdle: тот, кто ждёт action (кнопка
    // «В корзину»), должен увидеть новое состояние до снятия спиннера.
    commit('setItems', response.items || [])
    commit('setCanOrder', response.canOrder || false)
    commit('setBonus', response.bonus || 0)
    commit('setProgressBar', response.progressBar || null)
    commit('setMessage', response.message || null)
    commit('setLoading', false)
    commit('setFirstLoaded', true)
  },
  async toCart(
    { dispatch, commit },
    payload: IProductToCart | IProductToCart[]
  ): Promise<void> {
    commit('setLoading', true)
    try {
      const response = await this.$axios.$post(
        filterUrl(CART),
        normalizeCartPayload(payload)
      )
      analyticsReachGoal('goal_product_toCart')
      window.dataLayer = window.dataLayer || []

      if (response.cartAction && response.products && window.dataLayer) {
        window.dataLayer.push({
          ecommerce: {
            currencyCode: 'RUB',
            [response.cartAction]: {
              products: response.products || [],
            },
          },
        })
      }

      if (response.text) {
        Vue.notify({
          group: 'default',
          type: response.success ? 'info' : 'error',
          title: response.success ? false : 'Ошибка',
          text:
            response.text ||
            'Не удалось добавить товар в корзину. Попробуйте ещё раз',
        } as NotificationOptions)
      }

      await dispatch('getItems')
    } finally {
      commit('setLoading', false)
    }
  },
}
